import React from "react";
import makeStyles from "@mui/styles/makeStyles";
import PropTypes from "prop-types";
import { Button, Box, Typography } from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { NavLink } from "react-router-dom";
import Modal from "../Modal";
import { useTranslation } from "react-i18next";
import { performanceAchievements } from "../../routes/LearningCenterRoutes";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import ButtonGroup from "../Buttons/ButtonGroup";
import Tooltip from "../Tooltip/Tooltip";

const useStyles = makeStyles(theme => ({
  arrow: {
    marginLeft: theme.spacing(1)
  },
  emphasizedText: {
    color: theme.palette.text.primary
  }
}));

const getQuizType = survey => {
  const { t } = useTranslation();
  return survey
    ? t("contentViewer.contentViewerHeader.survey")
    : t("contentViewer.contentViewerHeader.quiz");
};

const CompletionMessageTitle = ({
  courseCompleted,
  nextModule,
  courseTitle,
  moduleTitle,
  isUnsubmittedQuiz,
  survey,
  moduleCompleted
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  if (isUnsubmittedQuiz) {
    return (
      <Box mb={2}>
        <Typography variant="h5" color="textSecondary">
          {t("contentViewer.shortCourseCompletedModal.unsubmittedQuiz", {
            quizType: getQuizType(survey)
          })}
        </Typography>
      </Box>
    );
  }
  return (
    <Box mb={2}>
      {moduleCompleted ? (
        <Typography variant="h5" color="textSecondary">
          {t("contentViewer.shortCourseCompletedModal.completionText")}{" "}
          <span className={classes.emphasizedText}>
            {courseCompleted && !nextModule ? courseTitle : moduleTitle}
          </span>
        </Typography>
      ) : (
        ""
      )}
    </Box>
  );
};

const ExternalSurveyButton = ({ externalSurveyLink }) => {
  const { t } = useTranslation();
  return externalSurveyLink ? (
    <Button color="primary" variant="text" className="link">
      <NavLink to={externalSurveyLink}>
        {t("contentViewer.shortCourseCompletedModal.takeSurveyBtn")}
      </NavLink>
    </Button>
  ) : null;
};

const InternalSurveyButton = ({ onInternalCourseSurvey }) => {
  const { t } = useTranslation();
  return (
    <Button color="primary" variant="text" onClick={onInternalCourseSurvey}>
      {t("contentViewer.shortCourseCompletedModal.takeSurveyBtn")}
    </Button>
  );
};

const ContinueButton = ({ onNextActivity, isUnsubmittedQuiz, survey }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Button
      onClick={onNextActivity}
      color="primary"
      variant="contained"
      autoFocus
    >
      {isUnsubmittedQuiz
        ? t("contentViewer.shortCourseCompletedModal.backToQuizBtn", {
            quizType: getQuizType(survey)
          })
        : t("contentViewer.shortCourseCompletedModal.continueBtn")}{" "}
      <ArrowRightAltIcon classes={{ root: classes.arrow }} />
    </Button>
  );
};

const ViewScoreButton = ({ recordsScore, isPreviewMode }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  let btnTxt;

  if (recordsScore) {
    btnTxt = "contentViewer.shortCourseCompletedModal.viewScoreBtn";
  } else {
    btnTxt = "contentViewer.shortCourseCompletedModal.viewResultsBtn";
  }

  const viewScoreBtn = (
    <Button
      color="primary"
      variant="contained"
      onClick={() => dispatch(push(performanceAchievements))}
      disabled={isPreviewMode}
    >
      {t(btnTxt)}
      <ArrowRightAltIcon classes={{ root: classes.arrow }} />
    </Button>
  );
  return isPreviewMode ? (
    <Tooltip title={t("coursePreview.notAvailableCoursePreviewMsg")}>
      <div>{viewScoreBtn}</div>
    </Tooltip>
  ) : (
    viewScoreBtn
  );
};

const ExitButton = ({ nextModuleDuration, onExitClick, exitLink }) => {
  const { t } = useTranslation();
  if (exitLink) {
    return (
      <Button
        color="primary"
        variant="text"
        onClick={() => {
          window.location = exitLink;
        }}
      >
        {t("contentViewer.shortCourseCompletedModal.exit")}
      </Button>
    );
  }

  return (
    <Button color="primary" variant="text" onClick={() => onExitClick()}>
      {nextModuleDuration > 0
        ? t("contentViewer.shortCourseCompletedModal.exitAndSchedule")
        : t("contentViewer.shortCourseCompletedModal.exit")}
    </Button>
  );
};

const CompletionMessageContent = ({
  courseCompleted,
  nextModule,
  nextModuleDuration,
  courseTitle,
  externalSurveyLink,
  isUnsubmittedQuiz,
  unfinishedResourceSurvey,
  hasCertificate,
  survey,
  recordsScore,
  isCoursePassed
}) => {
  const { t } = useTranslation();
  function exitCourseMessageText() {
    if (recordsScore) {
      if (hasCertificate) {
        if (!!unfinishedResourceSurvey) {
          if (courseCompleted && isCoursePassed === true) {
            return t(
              `contentViewer.shortCourseCompletedModal.exitMsgText.surveyScore`
            );
          } else if (courseCompleted && isCoursePassed === false) {
            return t(
              `contentViewer.shortCourseCompletedModal.exitMsgText.surveyNoCertScore`
            );
          }
        } else {
          if (courseCompleted && isCoursePassed === true) {
            return t(
              `contentViewer.shortCourseCompletedModal.exitMsgText.courseCompletedScore`
            );
          } else if (courseCompleted && isCoursePassed === false) {
            return t(
              `contentViewer.shortCourseCompletedModal.exitMsgText.courseCompletedNoCertScore`
            );
          }
        }
      } else {
        if (!!unfinishedResourceSurvey) {
          if (courseCompleted && isCoursePassed === true) {
            return t(
              `contentViewer.shortCourseCompletedModal.exitMsgText.surveyNoCertScore`
            );
          } else if (courseCompleted && isCoursePassed === false) {
            return t(
              `contentViewer.shortCourseCompletedModal.exitMsgText.surveyNoCertScore`
            );
          }
        } else {
          if (courseCompleted && isCoursePassed === true) {
            return t(
              `contentViewer.shortCourseCompletedModal.exitMsgText.courseCompletedNoCertScore`
            );
          } else if (courseCompleted && isCoursePassed === false) {
            return t(
              `contentViewer.shortCourseCompletedModal.exitMsgText.courseCompletedNoCertScore`
            );
          }
        }
      }
    } else {
      if (hasCertificate) {
        if (!!unfinishedResourceSurvey) {
          if (courseCompleted) {
            return t(
              `contentViewer.shortCourseCompletedModal.exitMsgText.survey`
            );
          }
        } else {
          if (courseCompleted) {
            return t(
              `contentViewer.shortCourseCompletedModal.exitMsgText.courseCompleted`
            );
          }
        }
      } else {
        if (!!unfinishedResourceSurvey) {
          if (courseCompleted) {
            return t(
              `contentViewer.shortCourseCompletedModal.exitMsgText.surveyNoCert`
            );
          }
        } else {
          if (courseCompleted) {
            return t(
              `contentViewer.shortCourseCompletedModal.exitMsgText.courseCompletedNoCert`
            );
          }
        }
      }
    }
  }
  return (
    <Typography variant="body1" color="textSecondary">
      {(courseCompleted && !nextModule) || isUnsubmittedQuiz
        ? exitCourseMessageText()
        : t("contentViewer.shortCourseCompletedModal.exitMsgText.schedule", {
            scheduleOrExit:
              nextModuleDuration > 0
                ? t("contentViewer.shortCourseCompletedModal.schedule")
                : t(
                    "contentViewer.shortCourseCompletedModal.exit"
                  ).toLowerCase() +
                  " " +
                  courseTitle
          })}
    </Typography>
  );
};

const NextModuleDescription = ({
  courseCompleted,
  nextModule,
  duration,
  isUnsubmittedQuiz
}) => {
  const { t } = useTranslation();
  return !courseCompleted && !isUnsubmittedQuiz && nextModule ? (
    <Box display="flex" justifyContent="space-between" mt={5}>
      <Typography variant="body1" color="textSecondary">
        {t("contentViewer.shortCourseCompletedModal.nextCourse")}
        {":"} {nextModule}
      </Typography>
      <Typography variant="body1" color="textSecondary" className="italic">
        {duration}
      </Typography>
    </Box>
  ) : null;
};

// TODO: split this up into separate modals

function ShortCourseCompletedModal(props) {
  const {
    courseCompleted,
    moduleCompleted,
    nextModule,
    courseTitle,
    moduleTitle,
    nextModuleDuration,
    externalSurveyLink,
    isUnsubmittedQuiz,
    survey,
    hasCertificate,
    showDialog,
    onNextActivity,
    close,
    exitLink,
    unfinishedResourceSurvey,
    onInternalCourseSurvey,
    onExitToLearningPath,
    recordsScore,
    isCoursePassed,
    isPreviewMode,
    fetchingConmpletions
  } = props;

  function duration() {
    if (nextModuleDuration > 0) {
      let minDuration = nextModuleDuration > 6 ? nextModuleDuration - 5 : 1;
      return `${minDuration}-${nextModuleDuration} min.`;
    } else {
      return "";
    }
  }

  function getDialogContent() {
    if (fetchingConmpletions && !moduleCompleted) {
      return <LoadingSpinner size={50} containerHeight={120} />;
    }
    return (
      <>
        <CompletionMessageTitle
          courseCompleted={courseCompleted}
          nextModule={nextModule}
          courseTitle={courseTitle}
          moduleTitle={moduleTitle}
          isUnsubmittedQuiz={isUnsubmittedQuiz}
          survey={survey}
          moduleCompleted={moduleCompleted}
          fetchingConmpletions={fetchingConmpletions}
        />
        <CompletionMessageContent
          courseCompleted={courseCompleted}
          nextModule={nextModule}
          nextModuleDuration={nextModuleDuration}
          courseTitle={courseTitle}
          externalSurveyLink={externalSurveyLink}
          isUnsubmittedQuiz={isUnsubmittedQuiz}
          survey={survey}
          unfinishedResourceSurvey={unfinishedResourceSurvey}
          hasCertificate={hasCertificate}
          recordsScore={recordsScore}
          isCoursePassed={isCoursePassed}
        />
        <NextModuleDescription
          duration={duration()}
          courseCompleted={courseCompleted}
          nextModule={nextModule}
          isUnsubmittedQuiz={isUnsubmittedQuiz}
        />
      </>
    );
  }

  function getDialogTitle() {
    const { t } = useTranslation();
    if (fetchingConmpletions && !moduleCompleted) {
      return (
        <Typography variant="h4">
          {t("contentViewer.shortCourseCompletedModal.verifyingProgress")}
        </Typography>
      );
    }
    return moduleCompleted && !isUnsubmittedQuiz ? (
      <Typography variant="h4">
        {t("contentViewer.shortCourseCompletedModal.congratulations")}
      </Typography>
    ) : null;
  }

  function getDialogActions() {
    if (fetchingConmpletions && !moduleCompleted) {
      return null;
    }
    return (
      <Box display="flex" flex={1} mt={6.5} justifyContent="flex-end">
        <ButtonGroup>
          {courseCompleted && !nextModule ? (
            <>
              <ExitButton
                nextModuleDuration={nextModuleDuration}
                exitLink={exitLink}
                onExitClick={onExitToLearningPath}
              />
              <ExternalSurveyButton externalSurveyLink={externalSurveyLink} />
              {unfinishedResourceSurvey && (
                <InternalSurveyButton
                  onInternalCourseSurvey={onInternalCourseSurvey}
                />
              )}
              <ViewScoreButton
                isPreviewMode={isPreviewMode}
                recordsScore={recordsScore}
              />
            </>
          ) : (
            <>
              <Box ml={-2}>
                <ExitButton
                  nextModuleDuration={nextModuleDuration}
                  onExitClick={onExitToLearningPath}
                />
              </Box>
              <ContinueButton
                onNextActivity={isUnsubmittedQuiz ? close : onNextActivity}
                isUnsubmittedQuiz={isUnsubmittedQuiz}
                survey={survey}
              />
            </>
          )}
        </ButtonGroup>
      </Box>
    );
  }

  return (
    <Modal
      open={showDialog}
      dialogActions={getDialogActions()}
      dialogTitle={getDialogTitle()}
      dialogContent={getDialogContent()}
    />
  );
}

export default ShortCourseCompletedModal;

ShortCourseCompletedModal.propTypes = {
  contentLink: PropTypes.string,
  nextModule: PropTypes.string,
  nextModuleDuration: PropTypes.number,
  moduleCompleted: PropTypes.bool,
  courseCompleted: PropTypes.bool,
  fetchingConmpletions: PropTypes.bool,
  courseTitle: PropTypes.string,
  moduleTitle: PropTypes.string,
  isUnsubmittedQuiz: PropTypes.bool,
  survey: PropTypes.bool,
  hasCertificate: PropTypes.bool,
  showDialog: PropTypes.bool,
  onNextActivity: PropTypes.func,
  close: PropTypes.func,
  exitLink: PropTypes.string,
  unfinishedResourceSurvey: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number
  ]),
  onInternalCourseSurvey: PropTypes.func,
  onExitToLearningPath: PropTypes.func
};
